import React, {useEffect, useRef, useState} from 'react';
import {Box, Stack, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {stripHtmlTags} from './AdditionalNotes';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {validateEmail, validatePhoneNumber} from '../../../../../utility/validationUtil';

const inputTypesByKey = {
    text: {
        label: 'Text',
        validate: null
    },
    email: {
        label: 'Email',
        validate: validateEmail
    },
    tel: {
        label: 'Phone',
        validate: validatePhoneNumber
    }
};

const SingleLine = ({
                        index,
                        id,
                        fieldKey,
                        value,
                        onChange,
                        description,
                        enableValidation,
                        isEditMode,
                        isFormArchived,
                        readonly,
                        placeholderValues,
                        accessLevel,
                        onUpdateDescription,
                        componentToEditIndex,
                        submitMode,
                        type,
                        error
                    }) => {
    const [currentValue, setCurrentValue] = useState(description);
    const prevPropRef = useRef();

    useEffect(() => {
        setCurrentValue(description);
    }, [description]);

    useEffect(() => {
        if (prevPropRef.current === index && componentToEditIndex !== prevPropRef.current) {
            onUpdateDescription({index, description: stripHtmlTags(currentValue), formattedDescription: currentValue});
        }
        prevPropRef.current = componentToEditIndex;
    }, [componentToEditIndex]);

    return (
        <Box key={`SingleLine_${id}`} sx={{mt: 2}}>
            {isEditMode
                ? <>
                    <RichEditor value={description} onChange={onChange} id={id} index={index} isEditMode={isEditMode}
                                isFormLive={isFormArchived} accessLevel={accessLevel} currentDescription={currentValue}
                                setCurrentDescription={setCurrentValue}/>

                    <Stack flexDirection="row"
                           justifyContent="space-between"
                           gap={3}
                           mb={2}
                    >
                        <RequiredCheckbox enableValidation={enableValidation} index={index} onChange={onChange}
                                          isFormArchived={isFormArchived || !!fieldKey}/>
                    </Stack>
                    <FormControl fullWidth>
                        <InputLabel id="input-type-select-label">Input type</InputLabel>
                        <Select
                            labelId="input-type-select-label"
                            id="input-type-select"
                            value={type}
                            disabled={!!fieldKey}
                            defaultValue="text"
                            label="Input type"
                            onChange={(e) => onChange({index, inputType: e.target.value})}
                        >
                            {Object.entries(inputTypesByKey).map(([key, config]) => (
                                <MenuItem key={key} value={key}>
                                    {config.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
                : <>
                    <RichEditorResultText id={id} description={description} placeholderValues={placeholderValues}
                                          readonly={readonly} enableValidation={enableValidation}/>
                    {!readonly
                        ? <TextField
                            fullWidth
                            value={value}
                            required={enableValidation}
                            disabled={!submitMode}
                            helperText={error}
                            error={!!error}
                            onChange={e => onChange({id, value: e.target.value})}
                            type={type || 'text'}
                        />
                        : <Typography sx={{
                            width: {xs: '80vw', sm: '60vw', md: '45vw', lg: '45vw', xl: '45vw'},
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                        }}>
                            {value}
                        </Typography>
                    }
                </>
            }
        </Box>
    );
};

SingleLine.propTypes = {
    id: PropTypes.string,
    fieldKey: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    validation: PropTypes.object,
    description: PropTypes.string,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    onUpdateDescription: PropTypes.func,
    componentToEditIndex: PropTypes.number,
    submitMode: PropTypes.bool,
    type: PropTypes.string,
    error: PropTypes.string
};

export default SingleLine;